import { ModelRow, StringUtil } from "@mcleod/core";
import { RowEdiLtxexpressProfFields } from "./models/autogen/AutogenModelEdiLtxexpressProf";

interface IncludeExcludeList {
    isExclude: boolean;
    origDestType?: string;
    dataField: string;
    values: string[];
}

export class LoadTenderExpressFilter {
    private _filterRow: ModelRow;
    private includeLists: IncludeExcludeList[] = [];
    public INCLUDE:string  = "I";
    public EXCLUDE: string  = "E";
    private ACTIVE = "A";
    private TEST = "T";
    private NOT_ACTIVE = "N";
    private ACTIVE_OR_TEST = "1";
    private ACTIVE_OR_NOT_ACTIVE = "2";
    private TEST_OR_NOT_ACTIVE = "3";

    constructor(row: ModelRow) {
        this._filterRow = row;
        this.addIncludeExcludeList("revenue_codes", "include_revenue", "revenue_code_id");
        this.addIncludeExcludeList("partner_ids", "include_partners", "partner_id");
        this.addIncludeExcludeList("customer_ids", "include_customers", "customer_id");
    }

    private addIncludeExcludeList(includeExcludeListFieldName: keyof RowEdiLtxexpressProfFields, includeExcludeFlagFieldName?: string, dataFieldName?: string) {
        const includeExcludeFlag = this._filterRow.getString(includeExcludeFlagFieldName , this.INCLUDE);
        this.includeLists.push({
            isExclude: includeExcludeFlag === this.EXCLUDE,
            values: this.tokenizeField(includeExcludeListFieldName, false, ","),
            dataField: dataFieldName || includeExcludeListFieldName
        });
    }
     private tokenizeField(field: keyof RowEdiLtxexpressProfFields, useZoneParsing: boolean, delimiter: string = ","): string[] {
            if (this._filterRow.isNull(field))
                return [];
            const result = this._filterRow.get(field).split(delimiter);
            for (let i = 0; i < result.length; i++)
                result[i] = result[i].trim();
            return result;
    }
    public dataPasses(data: ModelRow): boolean {
        for (const list of this.includeLists) {
            const listMatches = this.findItem(list.values, data.get(list.dataField));
            if ((list.isExclude && listMatches) || (!list.isExclude && !listMatches))
                return false;
        }
        //origin zone filter
        const origType = this._filterRow.getString("orig_type");
        let origTypeFieldName: string = "";
        const includeOrig = "I" === this._filterRow.getString("include_orig", "I");//I or E
        const origValues = this.tokenizeField("orig_zones", true, ",");//delimited list of states, zones, zip codes, or locations
        const includeEmptyOrig = "Y" === this._filterRow.getString("include_empty_orig", "Y");
        if(!StringUtil.isEmptyString(origType)){
            if("S" === origType){
               //state
               origTypeFieldName = "origin_state";
            }else if("Z" === origType){
                //origin zones
                origTypeFieldName = "origin_zone";
            }else if("C" === origType){
                //origin zip codes
                origTypeFieldName = "origin_zip";
            }else{
                // location codes
                origTypeFieldName = "origin_location_id";
            }
            const listMatches = this.findItem(origValues, data.get(origTypeFieldName), includeEmptyOrig);
            if ((includeOrig && !listMatches) || (!includeOrig && listMatches))
                return false;
        }
        //dest zone filter
        const destType = this._filterRow.getString("dest_type");
        const destValues = this.tokenizeField("dest_zones", true, ",");
        const includeDest = "I" === this._filterRow.getString("include_dest", "I");
        const includeEmptyDest = "Y" === this._filterRow.getString("include_empty_dest", "Y");
        let destTypeFieldName: string = "";
         if("S" === destType){
            //state
            destTypeFieldName = "destination_state";
          }else if("Z" === destType){
              //destination zones
              destTypeFieldName = "destination_zone";
          }else if("C" === destType){
              //destination zip codes
              destTypeFieldName = "destination_zip";
          }else{
             // location codes
             destTypeFieldName = "destination_location_id";
         }
         const listMatches = this.findItem(destValues, data.get(destTypeFieldName), includeEmptyDest);
         if ((includeDest && !listMatches) || (!includeDest && listMatches))
             return false;
        //original tenders
        const includeOriginals = "Y" === this._filterRow.getString("show_original");
        if(!includeOriginals && "Y" === data.get("is_original") )
            return false;
        //change tenders
        const includeChange = "Y" === this._filterRow.getString("show_change");
        if(!includeChange && "Y" === data.get("is_change") )
            return false;
        //cancel tenders
        const includeCancel = "Y" === this._filterRow.getString("show_cancel");
        if(!includeCancel && "Y" === data.get("is_cancel") )
            return false;
        //tenders with no replies
        const includeNoReplyNeeded = "Y" === this._filterRow.getString("no_reply_needed");
        if(!includeNoReplyNeeded && "N" === data.get("requires_reply"))
            return false;
        //marked for exclusion
        const includeNoDisplay = "Y" === this._filterRow.getString("no_display");
        if(!includeNoDisplay && "Y" === data.get("no_display"))
            return false;
        //days previous
        const daysPrevious = this._filterRow.getNumber("days_previous");
        const createDate = data.get("create_date");
        if(!this.isDateValid(createDate, daysPrevious))
            return false;
         //add logic for partner status
        const partnerStatus = this._filterRow.getString("partner_status");
        if(this.ACTIVE === partnerStatus && this.ACTIVE !== data.get("partner_status"))//must be Active
            return false;
        else if(this.TEST === partnerStatus && this.TEST !== data.get("partner_status"))//must be in test
            return false;
        else if(this.NOT_ACTIVE === partnerStatus && this.NOT_ACTIVE !== data.get("partner_status"))//must be not active
            return false;
        else if(this.ACTIVE_OR_TEST === partnerStatus && ((this.ACTIVE !== data.get("partner_status")) && this.TEST !== data.get("partner_status")))//must active or in test
            return false;
        else if(this.ACTIVE_OR_NOT_ACTIVE === partnerStatus && ((this.ACTIVE !== data.get("partner_status")) && this.NOT_ACTIVE !== data.get("partner_status")))//must be active or not active
            return false;
        else if(this.TEST_OR_NOT_ACTIVE === partnerStatus && ((this.TEST !== data.get("partner_status")) && this.NOT_ACTIVE !== data.get("partner_status")))//must be in test or inactive
            return false;
        //todo add logic for intercompany filter
        const intercompany = this._filterRow.getString("intercompany_filter");
        if("I" === intercompany && !data.get("intercompany"))
            return false;
        else if("N" === intercompany && data.get("intercompany"))
            return false;
        return true;
    }
    protected isDateValid(createDate: string, daysPrevious: number): boolean {
        const createDateObj = new Date(createDate);
        const today = new Date();
        const comparisonDate = new Date();
        comparisonDate.setDate(today.getDate() - daysPrevious);

        return createDateObj >= comparisonDate;
    }
    protected findItem(itemList: string[], item: string, includeEmpty?: boolean): boolean {
        if (itemList == null || itemList.length === 0 || "*" === itemList[0])
            return true;
        if (StringUtil.isEmptyString(item) && includeEmpty)
            return true;
        if (StringUtil.isEmptyString(item))
            return false;
        return itemList.indexOf(item) >= 0;
    }

}
